/* eslint-disable @typescript-eslint/no-explicit-any -- same signatures as the frozen leasingTooltipContent.ts */
import { formatCurrency, toNumber, getAdjustmentBreakdown } from './leasingCalculations'

// English twin of leasingTooltipContent.ts (#714). That file is QA-frozen and
// stays byte-identical; this one mirrors it function by function, same
// markup, translated text. Callers go through leasingTooltips.ts, which picks
// one of the two by the current language.

// Listepris tooltip
export const getListeprisTooltip = (result: any): string => {
  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">List price</div>
      <div class="text-sm opacity-90">
        The car's list price incl. VAT, delivered Oslo area (excl. equipment)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(result.CarPrice)}</span>
      </div>
      ${result.CarPriceURL ? `
      <div class="mt-2 pt-2 border-t border-gray-700 text-xs opacity-75">
        Source: ${result.CarPriceURL}
      </div>
      ` : ''}
    </div>
  `
}

// Startleie + Etableringsgebyr tooltip
export const getInitialTotalTooltip = (result: any): string => {
  const initialPay = toNumber(result.InitialPay)
  const initialFee = toNumber(result.InitialFee)
  const total = initialPay + initialFee

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Initial payment + Establishment fee</div>
      <div class="text-sm opacity-90">
        Total amount paid at the start of the lease agreement
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="opacity-80">Initial payment: ${formatCurrency(initialPay)}</div>
        <div class="opacity-80">Establishment fee: ${formatCurrency(initialFee)}</div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(total)}</span>
      </div>
    </div>
  `
}

// Månedspris kampanje tooltip
export const getLeasePriceTooltip = (result: any): string => {
  const leasePrice = toNumber(result.LeasePrice)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Monthly price, campaign</div>
      <div class="text-sm opacity-90">
        Advertised monthly lease price in the campaign (excl. initial payment and establishment fee)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(leasePrice)}/mo</span>
      </div>
    </div>
  `
}

// Leasingpris 1 tooltip
export const getLeasingpris1Tooltip = (result: any): string => {
  const leasePrice = toNumber(result.LeasePrice)
  const duration = toNumber(result.LeaseDurationMonths)
  const initialPay = toNumber(result.InitialPay)
  const initialFee = toNumber(result.InitialFee)
  const totalLeasePrice1 = toNumber(result.TotalLeasePrice1)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Total price/mo (campaign)</div>
      <div class="text-sm opacity-90">
        Advertised campaign price per month (without adjustments)
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formula:</div>
        <div class="font-mono opacity-80">
          (Monthly price × Period + Initial payment + Establishment) ÷ Period
        </div>
        <div class="font-mono opacity-80">
          (${formatCurrency(leasePrice)} × ${duration} + ${formatCurrency(initialPay)} + ${formatCurrency(initialFee)}) ÷ ${duration}
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(totalLeasePrice1)}/mo</span>
      </div>
    </div>
  `
}

// Totalpris 1 tooltip
export const getTotalpris1Tooltip = (result: any): string => {
  const totalLeasePrice1 = toNumber(result.TotalLeasePrice1)
  const duration = toNumber(result.LeaseDurationMonths)
  const totalPrice1 = toNumber(result.TotalPrice1)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Total price 1</div>
      <div class="text-sm opacity-90">
        Total monthly price: Real total cost after spreading the initial payment and establishment fee
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formula:</div>
        <div class="font-mono opacity-80">
          Lease price 1 × Lease period
        </div>
        <div class="font-mono opacity-80">
          ${formatCurrency(totalLeasePrice1)} × ${duration} mo
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(totalPrice1)}</span>
      </div>
    </div>
  `
}

// Individual adjustment tooltips
export const getMetallicTooltip = (result: any): string => {
  const value = toNumber(result.AdjustMetallic)
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Metallic paint</div>
        <div class="text-sm opacity-90">No adjustment</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Metallic paint</div>
      <div class="text-sm opacity-90">
        Metallic cost financed over the lease period
      </div>
      <div class="mt-2 text-xs opacity-80">
        Calculated as an annuity based on the cost price of metallic and a residual value of 50% after 36 months.
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

export const getServiceTooltip = (result: any): string => {
  const value = toNumber(result.AdjustService)
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Service package</div>
        <div class="text-sm opacity-90">No adjustment</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Service package</div>
      <div class="text-sm opacity-90">
        Value of included maintenance and servicing
      </div>
      <div class="mt-2 text-xs opacity-80">
        Calculated as a percentage of the car's price (currently 0.051% per month)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

export const getTowingHitchTooltip = (result: any): string => {
  const value = toNumber(result.AdjustTowingHitch)
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Tow bar</div>
        <div class="text-sm opacity-90">No adjustment</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Tow bar</div>
      <div class="text-sm opacity-90">
        Value of included tow bar
      </div>
      <div class="mt-2 text-xs opacity-80">
        Calculated as a percentage of the car's price (currently 0,027% per month)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

export const getOppCostTooltip = (result: any): string => {
  const value = toNumber(result.AdjustOppCost)
  const initialPay = toNumber(result.InitialPay)
  const oppCostRate = result.InitialFeeOppCost ? toNumber(result.InitialFeeOppCost) : null

  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Opportunity cost</div>
        <div class="text-sm opacity-90">No initial payment - no opportunity cost</div>
      </div>
    `
  }

  const rateDisplay = oppCostRate !== null
    ? `<div class="text-xs opacity-80">
        Rate used: ${(oppCostRate * 100).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})}% (after tax)
      </div>`
    : ''

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Opportunity cost</div>
      <div class="text-sm opacity-90">
        Financial cost of capital tied up in the initial payment
      </div>
      <div class="mt-2 text-xs opacity-80">
        Initial payment: ${formatCurrency(initialPay)}
      </div>
      ${rateDisplay}
      <div class="text-xs opacity-80">
        Is lost interest income on the amount paid, after tax.
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

export const getMileageTooltip = (result: any): string => {
  const value = toNumber(result.AdjustMileage)
  const mileage = toNumber(result.Mileage)

  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Mileage</div>
        <div class="text-sm opacity-90">Standard mileage - no adjustment</div>
        <div class="text-xs opacity-80 mt-1">${mileage.toLocaleString('nb-NO')} km/year</div>
      </div>
    `
  }

  const isLow = value < 0

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Low mileage</div>
      <div class="text-sm opacity-90">
        ${isLow ? 'Deduction for fewer kilometres' : 'Surcharge for more kilometres'}
      </div>
      <div class="mt-2 text-xs opacity-80">
        Total included mileage: ${mileage.toLocaleString('nb-NO')} km
      </div>
      <div class="text-xs opacity-80">
        Below standard for this segment
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

export const getHighMileageTooltip = (result: any): string => {
  const value = toNumber(result.AdjustHighMileage)
  const mileage = toNumber(result.Mileage)

  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">High mileage</div>
        <div class="text-sm opacity-90">No adjustment</div>
      </div>
    `
  }

  const isReduction = value < 0

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">High mileage</div>
      <div class="text-sm opacity-90">
        ${isReduction ? 'Deduction for high mileage' : 'Surcharge for high mileage'}
      </div>
      <div class="mt-2 text-xs opacity-80">
        Total included mileage: ${mileage.toLocaleString('nb-NO')} km
      </div>
      <div class="text-xs opacity-80">
        Above standard for this car segment
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

export const getManualCorrectionTooltip = (result: any): string => {
  const value = toNumber(result.AdjustManualCorrection)
  const comment = result.CommentManualCorrection

  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Manual correction</div>
        <div class="text-sm opacity-90">No manual adjustment</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Manual correction</div>
      <div class="text-sm opacity-90">
        User-defined adjustment
      </div>
      ${comment ? `
        <div class="mt-2 text-xs opacity-80 italic">
          "${comment}"
        </div>
      ` : ''}
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mo
        </span>
      </div>
    </div>
  `
}

// "Justeringer" tooltip (#707), built exactly as in the Norwegian file.
export const getAdjustmentsTooltip = (result: { AdjustHighMileage: string | number | null }): string => {
  const mileageTooltip = toNumber(result.AdjustHighMileage) !== 0
    ? getHighMileageTooltip(result)
    : getMileageTooltip(result)

  const blocks = [
    getOppCostTooltip(result),
    getMetallicTooltip(result),
    getServiceTooltip(result),
    getTowingHitchTooltip(result),
    mileageTooltip,
    getManualCorrectionTooltip(result),
  ]
  return `<div class="grid grid-cols-2 gap-x-6 gap-y-3">${blocks.map(b => `<div>${b}</div>`).join('')}</div>`
}

// Leasingpris 2 tooltip
export const getLeasingpris2Tooltip = (result: any): string => {
  const totalLeasePrice1 = toNumber(result.TotalLeasePrice1)
  const totalLeasePrice2 = toNumber(result.TotalLeasePrice2)
  const adjustments = getAdjustmentBreakdown(result)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Total price/mo (adjusted)</div>
      <div class="text-sm opacity-90">
        Total price/mo (campaign) adjusted for all relevant adjustments
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="opacity-80">Base: ${formatCurrency(totalLeasePrice1)}/mo</div>
        ${adjustments.length > 0 ? `
          <div class="mt-2 space-y-1">
            <div class="opacity-80 font-medium">Adjustments:</div>
            ${adjustments.map(adj => `
              <div class="flex justify-between opacity-80">
                <span>• ${adj.label}:</span>
                <span class="${adj.value > 0 ? 'text-red-400' : 'text-green-400'}">
                  ${adj.value > 0 ? '+' : ''}${formatCurrency(adj.value)}
                </span>
              </div>
            `).join('')}
          </div>
        ` : '<div class="opacity-80">No adjustments</div>'}
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <div class="flex justify-between items-center">
          <span class="font-medium">Total:</span>
          <span class="font-semibold text-lg">${formatCurrency(totalLeasePrice2)}/mo</span>
        </div>
      </div>
    </div>
  `
}

// Totalpris 2 tooltip
export const getTotalpris2Tooltip = (result: any): string => {
  const totalLeasePrice2 = toNumber(result.TotalLeasePrice2)
  const duration = toNumber(result.LeaseDurationMonths)
  const totalPrice2 = toNumber(result.TotalPrice2)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Total price 2</div>
      <div class="text-sm opacity-90">
        Total leasing cost over the whole period
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formula:</div>
        <div class="font-mono opacity-80">
          Total price/mo (adjusted) × Lease period
        </div>
        <div class="font-mono opacity-80">
          ${formatCurrency(totalLeasePrice2)} × ${duration} mo
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(totalPrice2)}</span>
      </div>
    </div>
  `
}

// MonthlyLeaseFactor tooltip
export const getMonthlyLeaseFactorTooltip = (result: any): string => {
  if (!result.MonthlyLeaseFactor || !result.CarPrice || !result.TotalLeasePrice2) {
    return `
      <div class="space-y-2">
        <div class="font-semibold text-base">Monthly factor</div>
        <div class="text-sm opacity-90">
          Calculation not available
        </div>
      </div>
    `
  }

  const factor = toNumber(result.MonthlyLeaseFactor)
  const factorPercent = factor.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
  const totalLeasePrice2 = toNumber(result.TotalLeasePrice2)
  const carPrice = toNumber(result.CarPrice)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Monthly factor</div>
      <div class="text-sm opacity-90">
        Monthly total price (adjusted) as a percentage of the car's list price
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formula:</div>
        <div class="font-mono opacity-80">
          (Total price/mo (adjusted) ÷ List price) × 100
        </div>
        <div class="font-mono opacity-80">
          (${formatCurrency(totalLeasePrice2)} ÷ ${formatCurrency(carPrice)}) × 100
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${factorPercent}</span>
      </div>
      <div class="mt-2 text-xs opacity-80 italic">
        Lower factor = more competitive leasing. Typical value: 1.0-2.0
      </div>
    </div>
  `
}
